const JSON_HEADERS = { "Content-Type": "application/json" };

const adminMutation = (path, method, payload, password) => request(path, {
  method,
  headers: { ...JSON_HEADERS, "X-Admin-Password": password || "" },
  ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
});

const FIELD_LABELS = {
  reply_signature: "签名纯文本",
  reply_signature_html: "签名富文本内容",
  data_url: "签名图片",
  email_password: "邮箱授权码",
  ai_api_key: "AI API Key",
  search_api_key: "联网搜索 API Key",
  doubao_search_api_key: "豆包搜索 API Key",
};

function formatValidationItem(item) {
  if (!item || typeof item !== "object") return String(item || "");
  const loc = Array.isArray(item.loc) ? item.loc : [];
  const field = loc[loc.length - 1];
  const label = FIELD_LABELS[field] || field || "字段";
  const message = item.msg || item.message || "内容不符合要求";
  if (field === "reply_signature_html" && /at most 20000|too_long|string_too_long/i.test(message)) {
    return "签名内容过大。请压缩图片，或使用签名工具栏上传图片后再保存。";
  }
  if (field === "reply_signature" && /at most 4000|too_long|string_too_long/i.test(message)) {
    return "签名文字过长，请精简签名介绍或移到公司介绍中。";
  }
  return `${label}：${message}`;
}

function formatErrorMessage(payload) {
  const detail = payload?.detail ?? payload?.message ?? payload;
  if (!detail) return "请求失败，请稍后重试。";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(formatValidationItem).filter(Boolean).join("；") || "请求内容不符合要求。";
  if (typeof detail === "object") {
    if (typeof detail.message === "string") return detail.message;
    try { return JSON.stringify(detail); } catch { return "请求内容不符合要求。"; }
  }
  return String(detail);
}

async function request(path, options = {}) {
  const { headers, ...fetchOptions } = options;
  let response;
  try {
    response = await fetch(path, {
      credentials: "include",
      headers: { Accept: "application/json", ...(headers || {}) },
      ...fetchOptions,
    });
  } catch (error) {
    throw new Error(`网络连接失败：${error.message || "请检查服务是否正在运行"}`);
  }
  const text = await response.text().catch(() => "");
  let payload = null;
  if (text.trim()) {
    try {
      payload = JSON.parse(text);
    } catch (parseError) {
      if (response.ok && /^\s*<!doctype html|^\s*<html/i.test(text)) {
        throw new Error(`接口返回了前端页面 HTML。请确认 /api 已指向 FastAPI：${response.url}`);
      }
      if (response.ok) throw new Error(`接口返回格式异常：${parseError.message || "JSON 解析失败"}`);
      payload = { detail: text.slice(0, 200) };
    }
  }
  if (!response.ok) {
    const error = new Error(formatErrorMessage(payload));
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
}

const jsonRequest = (path, method, payload) => request(path, {
  method,
  headers: JSON_HEADERS,
  body: JSON.stringify(payload),
});

const queryRequest = (path, params = {}, options = {}) => {
  const query = new URLSearchParams(
    Object.entries(params).filter(([, value]) => value !== "" && value !== null && value !== undefined),
  );
  return request(`${path}?${query}`, options);
};

export const api = {
  me: () => request("/api/auth/me"),
  login: (payload) => jsonRequest("/api/auth/login", "POST", payload),
  register: (payload) => jsonRequest("/api/auth/register", "POST", payload),
  logout: () => request("/api/auth/logout", { method: "POST" }),
  changePassword: (payload) => jsonRequest("/api/auth/change-password", "POST", payload),

  dashboard: () => request("/api/dashboard"),
  aiProviderAlert: () => request("/api/ai/provider-alert"),
  dashboardReport: (params = {}, options = {}) => queryRequest("/api/dashboard/report", params, options),
  joinfLatestCustomers: (operatorName = "") => queryRequest("/api/joinf/latest-customers", { operator_name: operatorName }),
  joinfCollectedCustomers: (params = {}) => queryRequest("/api/joinf/collected-customers", params),
  joinfCollectedCustomer: (sourceId) => queryRequest("/api/joinf/collected-customer", { source_id: sourceId }),
  joinfInquiryContext: (inquiryId) => request(`/api/joinf/inquiry-context/${inquiryId}`),
  systemLogs: (params = {}) => queryRequest("/api/system-logs", params),
  settings: () => request("/api/settings"),
  saveSettings: (payload) => jsonRequest("/api/settings", "PUT", payload),
  uploadSignatureImage: (payload) => jsonRequest("/api/settings/signature-image", "POST", payload),
  deleteSettings: () => request("/api/settings", { method: "DELETE" }),
  testEmail: () => request("/api/settings/test-email", { method: "POST" }),
  testAi: () => request("/api/settings/test-ai", { method: "POST" }),
  testSearch: () => request("/api/settings/test-search", { method: "POST" }),

  whatsappOverview: () => request("/api/whatsapp/overview"),
  whatsappSimulation: () => request("/api/whatsapp/simulation"),
  startWhatsAppSimulation: () => request("/api/whatsapp/simulation", { method: "POST" }),
  sendWhatsAppSimulation: (payload) => jsonRequest("/api/whatsapp/simulation/messages", "POST", payload),
  saveWhatsAppSettings: (payload) => jsonRequest("/api/whatsapp/settings", "PUT", payload),
  claimWhatsAppAccount: () => request("/api/whatsapp/account/claim", { method: "POST" }),
  logoutWhatsAppAccount: () => request("/api/whatsapp/account/logout", { method: "POST" }),
  whatsappConversation: (id) => request(`/api/whatsapp/conversations/${id}`),
  whatsappConversationAction: (id, action) => jsonRequest(`/api/whatsapp/conversations/${id}/action`, "POST", { action }),

  productKnowledge: () => request("/api/product-knowledge"),
  productKnowledgeSource: (id) => request(`/api/product-knowledge/sources/${id}`),
  seedProductKnowledge: () => request("/api/product-knowledge/defaults", { method: "POST" }),
  createProductKnowledgeSource: (payload) => jsonRequest("/api/product-knowledge/sources", "POST", payload),
  updateProductKnowledgeSource: (id, payload) => jsonRequest(`/api/product-knowledge/sources/${id}`, "PUT", payload),
  syncProductKnowledge: () => request("/api/product-knowledge/sync", { method: "POST" }),
  reindexProductKnowledge: () => request("/api/product-knowledge/reindex", { method: "POST" }),
  syncProductKnowledgeSource: (id) => request(`/api/product-knowledge/sources/${id}/sync`, { method: "POST" }),
  updateProductKnowledgeStatus: (id, status) => jsonRequest(`/api/product-knowledge/sources/${id}/status`, "PUT", { status }),
  deleteProductKnowledgeSource: (id) => request(`/api/product-knowledge/sources/${id}`, { method: "DELETE" }),
  knowledgeConflicts: (status = "") => queryRequest("/api/knowledge-conflicts", { status }),
  updateKnowledgeConflict: (id, payload) => jsonRequest(`/api/knowledge-conflicts/${id}`, "PATCH", payload),
  researchSources: (params = {}) => queryRequest("/api/research-sources", params),
  createResearchSource: (payload) => jsonRequest("/api/research-sources", "POST", payload),
  updateResearchSource: (id, payload) => jsonRequest(`/api/research-sources/${id}`, "PATCH", payload),
  enableResearchSource: (id) => request(`/api/research-sources/${id}/enable`, { method: "POST" }),
  disableResearchSource: (id) => request(`/api/research-sources/${id}/disable`, { method: "POST" }),
  seedResearchSources: () => request("/api/research-sources/seed", { method: "POST" }),
  researchSourceUsage: (id) => request(`/api/research-sources/${id}/usage`),
  industryIntelligence: (params = {}) => queryRequest("/api/industry-intelligence", params),
  industryIntelligenceStats: () => request("/api/industry-intelligence/stats"),
  industryIntelligenceStatus: () => request("/api/industry-intelligence/status"),
  industryIntelligenceItem: (id) => request(`/api/industry-intelligence/${id}`),
  syncIndustryIntelligence: () => request("/api/industry-intelligence/sync", { method: "POST" }),
  inquiryResearchDetail: (id) => request(`/api/inquiries/${id}/research-detail`),

  inquiries: (params = {}) => queryRequest("/api/inquiries", params),
  inquiry: (id, compact = false) => request(`/api/inquiries/${id}${compact ? "?compact=1" : ""}`),
  inquiryBodyHtml: (id) => request(`/api/inquiries/${id}/body-html`),
  sync: () => request("/api/inquiries/sync", { method: "POST" }),
  generate: (id, payload = null) => payload
    ? jsonRequest(`/api/inquiries/${id}/generate`, "POST", payload)
    : request(`/api/inquiries/${id}/generate`, { method: "POST" }),
  research: (id) => request(`/api/inquiries/${id}/research`, { method: "POST" }),
  prepareInquiry: (id) => request(`/api/inquiries/${id}/prepare`, { method: "POST" }),
  queueInquiryAi: (id) => request(`/api/inquiries/${id}/queue-ai`, { method: "POST" }),
  retryInquiry: (id) => request(`/api/inquiries/${id}/retry`, { method: "POST" }),
  translateInquiry: (id) => request(`/api/inquiries/${id}/translate`, { method: "POST" }),
  researchPrompts: () => request("/api/research-prompts"),
  createResearchPrompt: (payload) => jsonRequest("/api/research-prompts", "POST", payload),
  updateResearchPrompt: (id, payload) => jsonRequest(`/api/research-prompts/${id}`, "PUT", payload),
  activateResearchPrompt: (id) => request(`/api/research-prompts/${id}/activate`, { method: "POST" }),
  restoreDefaultResearchPrompt: () => request("/api/research-prompts/default/restore", { method: "POST" }),
  deleteResearchPrompt: (id) => request(`/api/research-prompts/${id}`, { method: "DELETE" }),
  saveDraft: (id, aiDraft) => jsonRequest(`/api/inquiries/${id}/draft`, "PUT", { ai_draft: aiDraft }),
  originalEmail: (id) => request(`/api/inquiries/${id}/original-email?_=${Date.now()}`, { cache: "no-store" }),
  saveInquiryBody: (id, body) => jsonRequest(`/api/inquiries/${id}/body`, "PUT", { body }),
  updateBusinessValue: (id, rating, reason = "") => jsonRequest(`/api/inquiries/${id}/business-value`, "PUT", { business_value_rating: rating, reason }),
  customerContext: (id) => request(`/api/inquiries/${id}/customer-context`),
  updateCustomerFollowUp: (id, payload) => jsonRequest(`/api/inquiries/${id}/follow-up`, "PUT", payload),
  addCustomerNote: (id, content) => jsonRequest(`/api/inquiries/${id}/notes`, "POST", { content }),
  saveInquiryRecipients: (id, recipientEmails) => jsonRequest(`/api/inquiries/${id}/recipients`, "PUT", { recipient_emails: recipientEmails }),
  send: (id, payload) => jsonRequest(`/api/inquiries/${id}/send`, "POST", payload),
  ccSuggestions: () => request("/api/reply/cc-suggestions"),

  mailbox: (params = {}) => queryRequest("/api/mailbox", params),
  mailboxSummary: () => request("/api/mailbox/summary"),
  mailboxMessage: (id) => request(`/api/mailbox/${id}`),
  syncMailbox: () => request("/api/mailbox/sync", { method: "POST" }),
  markMailboxRead: (id) => request(`/api/mailbox/${id}/read`, { method: "POST" }),
  toggleMailboxStar: (id) => request(`/api/mailbox/${id}/star`, { method: "POST" }),

  adminOverview: () => request("/api/admin/overview"),
  joinfSyncStatus: () => request("/api/admin/joinf/sync-status"),
  adminCreateUser: (payload, confirmation) => adminMutation("/api/admin/users", "POST", payload, confirmation),
  adminUpdateUser: (id, payload, confirmation) => adminMutation(`/api/admin/users/${id}`, "PUT", payload, confirmation),
  adminDeleteUser: (id, confirmation) => adminMutation(`/api/admin/users/${id}`, "DELETE", undefined, confirmation),
  adminResetPassword: (id, password = null, confirmation) => adminMutation(`/api/admin/users/${id}/reset-password`, "POST", { password }, confirmation),
  adminDeleteSettings: (id, confirmation) => adminMutation(`/api/admin/users/${id}/settings`, "DELETE", undefined, confirmation),
};
